import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  X, Mail, Flame, AlertTriangle, Bot, Target, FolderKanban,
  History as HistoryIcon, RotateCcw, ExternalLink, FileText, Clock, DollarSign,
} from "lucide-react";
import {
  type ActionItem, type ActionKind, type ActionGroup, ACTION_GROUP_TABS, groupOf,
} from "../../lib/actionCenter";
import { useActionCenterStore } from "../../store/useActionCenterStore";

const KIND_ICON: Record<ActionKind, React.ElementType> = {
  "client-response": Mail,
  "quote-request": FileText,
  "lead-followup": Clock,
  "bid-price-review": DollarSign,
  "high-value": Flame,
  "proposal-followup": AlertTriangle,
  "project-review": FolderKanban,
  "ai-job": Bot,
  "recommended-category": Target,
};

const PRIORITY_LABEL: Record<string, string> = {
  critical: "High Priority",
  important: "Medium",
  info: "Info",
};

// Round avatar background + icon colour per kind (reference "Action Center" look).
const KIND_AVATAR: Record<ActionKind, string> = {
  "quote-request": "bg-primary/10 text-primary",
  "client-response": "bg-secondary-container text-on-secondary-container",
  "project-review": "bg-surface-container-highest text-on-surface-variant",
  "lead-followup": "bg-amber-100 text-amber-600",
  "proposal-followup": "bg-error-container text-error",
  "bid-price-review": "bg-amber-100 text-amber-600",
  "ai-job": "bg-emerald-100 text-emerald-600",
  "high-value": "bg-orange-100 text-orange-600",
  "recommended-category": "bg-primary/10 text-primary",
};

const PRIORITY_TAG: Record<string, string> = {
  critical: "text-error font-bold",
  important: "text-[#854d0e] font-semibold",
  info: "text-on-surface-variant font-medium",
};

export function ActionItemRow({ item }: { item: ActionItem }) {
  const { setStatus } = useActionCenterStore();
  const Icon = KIND_ICON[item.kind];

  const act = () => {
    if (!item.secondary) return;
    setStatus({
      id: item.id,
      status: item.secondary.status,
      at: Date.now(),
      title: item.title,
      kind: item.kind,
    });
    toast.success(
      item.secondary.status === "completed"
        ? "Marked complete"
        : item.secondary.status === "ignored"
        ? "Ignored"
        : "Dismissed"
    );
  };

  const Primary = () =>
    item.primary.href ? (
      <a
        href={item.primary.href}
        target="_blank"
        rel="noreferrer"
        className="px-2.5 py-1 bg-primary text-white text-[11px] font-semibold rounded hover:bg-[#004b9e] transition-colors inline-flex items-center gap-1"
      >
        {item.primary.label} <ExternalLink size={11} />
      </a>
    ) : (
      <Link
        to={item.primary.to || "#"}
        className="px-2.5 py-1 bg-primary text-white text-[11px] font-semibold rounded hover:bg-[#004b9e] transition-colors"
      >
        {item.primary.label}
      </Link>
    );

  return (
    <div className="flex items-center justify-between p-2.5 bg-surface-container-low rounded-lg border border-outline-variant hover:border-primary transition-colors gap-2">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${KIND_AVATAR[item.kind]}`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-on-surface truncate">
            {item.client ? <span>{item.client} | </span> : null}{item.title}
          </div>
          <div className="text-[13px] text-on-surface-variant truncate">
            {item.subtitle}
            <span className={`ml-1 text-[10px] uppercase ${PRIORITY_TAG[item.priority]}`}>• {PRIORITY_LABEL[item.priority]}</span>
          </div>
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Primary />
        {item.secondary && (
          <button
            onClick={act}
            className="px-2.5 py-1 text-on-surface-variant text-[11px] hover:bg-surface-container rounded transition-colors"
          >
            {item.secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}

export function ActionCenterModal({
  items,
  onClose,
}: {
  items: ActionItem[];
  onClose: () => void;
}) {
  const [tab, setTab] = useState<ActionGroup | "history">("notifications");
  const { history, reopen, clearHistory } = useActionCenterStore();

  const historyList = useMemo(
    () => Object.values(history).sort((a, b) => b.at - a.at),
    [history]
  );

  const filtered = useMemo(
    () => (tab === "history" ? items : items.filter((i) => groupOf(i) === tab)),
    [items, tab]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-container-lowest w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
          <h2 className="text-lg font-bold text-on-surface">Action Center</h2>
          <button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full">
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-1 px-4 pt-3 border-b border-outline-variant overflow-x-auto cre-scroll">
          {ACTION_GROUP_TABS.map((t) => {
            const count = items.filter((i) => groupOf(i) === t.key).length;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors border-b-2 ${
                  tab === t.key
                    ? "text-primary border-primary"
                    : "text-on-surface-variant border-transparent hover:text-on-surface"
                }`}
              >
                {t.label} {count > 0 && <span className="opacity-70">({count})</span>}
              </button>
            );
          })}
          <button
            onClick={() => setTab("history")}
            className={`px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors border-b-2 inline-flex items-center gap-1 ${
              tab === "history"
                ? "text-primary border-primary"
                : "text-on-surface-variant border-transparent hover:text-on-surface"
            }`}
          >
            <HistoryIcon size={13} /> History
          </button>
        </div>

        <div className="cre-scroll overflow-y-auto px-5 py-4 space-y-2.5 flex-1">
          {tab === "history" ? (
            historyList.length === 0 ? (
              <div className="text-center py-12 text-outline">No action history yet.</div>
            ) : (
              <>
                <div className="flex justify-end">
                  <button
                    onClick={clearHistory}
                    className="text-[11px] text-on-surface-variant hover:text-error"
                  >
                    Clear history
                  </button>
                </div>
                {historyList.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center justify-between p-2.5 bg-surface-container-low rounded-lg border border-outline-variant"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-on-surface truncate">{h.title}</div>
                      <div className="text-[11px] text-outline">
                        {new Date(h.at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-surface-container text-on-surface-variant">
                        {h.status}
                      </span>
                      <button
                        onClick={() => reopen(h.id)}
                        title="Reopen"
                        className="p-1 text-outline hover:text-primary"
                      >
                        <RotateCcw size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-outline">Nothing here right now. You're all caught up.</div>
          ) : (
            filtered.map((item) => <ActionItemRow key={item.id} item={item} />)
          )}
        </div>
      </div>
    </div>
  );
}
